import type { RowData } from "@tanstack/react-table";
import type { DataGridTable } from "../../columns/types";
import {
    getZoneValueColumnIds,
    isValueColumn,
    moveColumn,
} from "../../toolbar/columnSettingsActions";
import {
    type ColumnDropTarget,
    getColumnDropTarget,
    getColumnMoveIndex,
} from "./columnDropTarget";

/** The vertical line between headers, in viewport content coordinates. */
export interface ColumnIndicatorState {
    target: ColumnDropTarget;
    x: number;
    top: number;
    height: number;
}

/**
 * The source's siblings a header drop can land between: shown value columns
 * in the same pin zone under the same group header, in display order.
 */
export const getSiblingColumnIds = <TData extends RowData>(
    table: DataGridTable<TData>,
    columnId: string,
) => {
    const source = table.getColumn(columnId);
    if (!source) {
        return [];
    }
    const pin = source.getIsPinned();
    return table
        .getVisibleLeafColumns()
        .filter(
            (column) =>
                column.getIsPinned() === pin &&
                column.parent?.id === source.parent?.id &&
                isValueColumn(column),
        )
        .map(({ id }) => id);
};

/** A column's header cell in the viewport. */
export const findHeader = (viewport: HTMLElement, columnId: string) =>
    viewport.querySelector<HTMLElement>(
        `[role="columnheader"][data-column-id="${CSS.escape(columnId)}"]`,
    );

/** The drop gap nearest `clientX`, measured from the live header cells. */
export const getColumnIndicatorAt = ({
    viewport,
    columnIds,
    sourceId,
    clientX,
}: {
    viewport: HTMLElement;
    columnIds: readonly string[];
    sourceId: string;
    clientX: number;
}): ColumnIndicatorState | null => {
    const slots = columnIds.flatMap((id) => {
        const rect = findHeader(viewport, id)?.getBoundingClientRect();
        return rect ? [{ id, start: rect.left, end: rect.right }] : [];
    });
    const target = getColumnDropTarget({ slots, sourceId, x: clientX });
    if (!target) {
        return null;
    }
    const viewportLeft = viewport.getBoundingClientRect().left;
    return {
        target,
        x: target.x - viewportLeft + viewport.scrollLeft,
        top: viewport.scrollTop,
        height: viewport.clientHeight,
    };
};

/** Applies a header drop through the same seam the settings list uses. */
export const dropColumn = <TData extends RowData>({
    table,
    sourceId,
    target,
    columnIds,
}: {
    table: DataGridTable<TData>;
    sourceId: string;
    target: ColumnDropTarget;
    columnIds: readonly string[];
}) => {
    const zoneColumnIds = getZoneValueColumnIds(table).find((ids) =>
        ids.includes(sourceId),
    );
    const lastSlotId = columnIds[columnIds.length - 1];
    if (!zoneColumnIds || lastSlotId === undefined) {
        return;
    }
    moveColumn(table, {
        columnId: sourceId,
        toIndex: getColumnMoveIndex({
            zoneColumnIds,
            sourceId,
            target,
            lastSlotId,
        }),
    });
};
